import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import PdfDocumentPreview from "@/components/shared/PdfDocumentPreview";

const escapeRegExp = (value = "") => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const renderTextWithHighlight = (text = "", highlightText = "", highlightStart = null, highlightEnd = null, markRef = null) => {
  if (Number.isInteger(highlightStart) && Number.isInteger(highlightEnd) && highlightEnd > highlightStart && highlightStart >= 0 && highlightEnd <= text.length) {
    return (
      <>
        {text.slice(0, highlightStart)}
        <mark ref={markRef} className="rounded bg-amber-200 px-1 py-0.5 text-slate-900 ring-1 ring-amber-300">
          {text.slice(highlightStart, highlightEnd)}
        </mark>
        {text.slice(highlightEnd)}
      </>
    );
  }

  if (!highlightText) return text;

  const exactIndex = text.indexOf(highlightText);
  if (exactIndex >= 0) {
    return (
      <>
        {text.slice(0, exactIndex)}
        <mark ref={markRef} className="rounded bg-amber-200 px-1 py-0.5 text-slate-900 ring-1 ring-amber-300">
          {text.slice(exactIndex, exactIndex + highlightText.length)}
        </mark>
        {text.slice(exactIndex + highlightText.length)}
      </>
    );
  }

  const regex = new RegExp(`(${escapeRegExp(highlightText)})`, "i");
  const parts = text.split(regex);
  if (parts.length <= 1) return text;

  return parts.map((part, idx) => (
    regex.test(part)
      ? <mark key={`${part}-${idx}`} ref={idx === 1 ? markRef : null} className="rounded bg-amber-200 px-1 py-0.5 text-slate-900 ring-1 ring-amber-300">{part}</mark>
      : <React.Fragment key={`${part}-${idx}`}>{part}</React.Fragment>
  ));
};

const getPageGroups = (documentBlocks, language) => {
  if (!documentBlocks.length) return [];

  const hasRealPages = documentBlocks.some((block) => Number.isFinite(block.page_number));
  if (!hasRealPages) {
    return [{ key: "document-flow", label: language === "es" ? "Documento" : "Document", blocks: documentBlocks }];
  }

  const pages = new Map();
  documentBlocks.forEach((block) => {
    const pageNumber = Number.isFinite(block.page_number) ? block.page_number : 0;
    if (!pages.has(pageNumber)) pages.set(pageNumber, []);
    pages.get(pageNumber).push(block);
  });

  return Array.from(pages.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([pageNumber, blocks]) => ({
      key: `page-${pageNumber}`,
      label: language === "es" ? `Página ${pageNumber}` : `Page ${pageNumber}`,
      blocks,
    }));
};

const getBlockWrapperStyle = (block) => ({
  marginLeft: `${Math.min(Number(block?.indent_px || 0), 120)}px`,
  width: `${Math.max(42, Math.min(Number(block?.width_pct || 100), 100))}%`,
  textAlign: block?.align || "left",
});

const getBlockClassName = (block, isHighlighted) => {
  const base = isHighlighted ? "bg-amber-50/90 ring-1 ring-amber-200" : "bg-transparent";
  if (block?.kind === "heading") return `rounded-xl px-3 py-2 text-[1.42rem] font-semibold leading-[1.5] tracking-[-0.01em] text-slate-900 ${base}`;
  if (block?.kind === "quote") return `rounded-xl border-l-4 border-slate-300 px-4 py-3 text-[1.02rem] italic leading-8 text-slate-700 ${base}`;
  if (block?.kind === "list_item") return `rounded-xl px-3 py-1.5 text-[1rem] leading-8 text-slate-800 ${base}`;
  return `rounded-xl px-3 py-1.5 text-[1.02rem] leading-8 text-slate-800 ${base}`;
};

export const DocumentViewer = forwardRef(function DocumentViewer({
  documentBlocks = [],
  highlightedBlockId = null,
  highlightedExcerpt = "",
  highlightedExcerptStart = null,
  highlightedExcerptEnd = null,
  language = "es",
  pdfPreviewUrl = null,
  testIdPrefix = "document-viewer",
}, ref) {
  const blockRefs = useRef({});
  const highlightMarkRef = useRef(null);

  const groupedPages = useMemo(() => getPageGroups(documentBlocks, language), [documentBlocks, language]);
  const highlightedPdfPageNumber = useMemo(() => {
    if (!pdfPreviewUrl || !highlightedBlockId) return null;
    const target = documentBlocks.find((block) => block.id === highlightedBlockId);
    return Number.isFinite(target?.page_number) ? target.page_number : null;
  }, [documentBlocks, highlightedBlockId, pdfPreviewUrl]);

  useImperativeHandle(ref, () => ({
    scrollToBlock: (blockId) => {
      if (pdfPreviewUrl) return;
      window.setTimeout(() => {
        const target = blockRefs.current[blockId];
        if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
    },
  }));

  useEffect(() => {
    if (pdfPreviewUrl) return;
    if (highlightMarkRef.current) {
      highlightMarkRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (highlightedBlockId && blockRefs.current[highlightedBlockId]) {
      blockRefs.current[highlightedBlockId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedBlockId, highlightedExcerpt, highlightedExcerptStart, highlightedExcerptEnd, pdfPreviewUrl]);

  if (!documentBlocks.length) {
    return <div className="p-6 text-center text-muted-foreground" data-testid="document-viewer-empty">{language === "es" ? "No hay contenido disponible" : "No content available"}</div>;
  }

  return (
    <div className="px-4 py-6 sm:px-6" data-testid="document-viewer">
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        {pdfPreviewUrl ? (
          <PdfDocumentPreview
            pdfPreviewUrl={pdfPreviewUrl}
            language={language}
            focusedPageNumber={highlightedPdfPageNumber}
            highlightedExcerpt={highlightedExcerpt}
            testIdPrefix={testIdPrefix}
          />
        ) : groupedPages.map((page) => (
          <section
            key={page.key}
            className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(0,0,0,0.18)]"
            data-testid={`document-page-${page.key}`}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 text-slate-500">
              <span className="text-xs uppercase tracking-[0.22em]" data-testid={`document-page-label-${page.key}`}>{page.label}</span>
              <span className="text-xs">{page.blocks.length} {language === "es" ? "bloques" : "blocks"}</span>
            </div>

            <div className="space-y-4 px-6 py-7 sm:px-10 sm:py-10">
              {page.blocks.map((block) => {
                const isHighlighted = highlightedBlockId === block.id;
                return (
                  <article
                    key={block.id}
                    ref={(node) => { blockRefs.current[block.id] = node; }}
                    className="transition-all duration-300"
                    style={getBlockWrapperStyle(block)}
                    data-testid={`document-block-${block.id}`}
                  >
                    <div className="mb-1 text-[11px] uppercase tracking-[0.18em] text-slate-400" data-testid={`document-block-label-${block.id}`}>
                      {block.label}
                    </div>
                    <div className={getBlockClassName(block, isHighlighted)}>
                      {isHighlighted && highlightedExcerpt
                        ? renderTextWithHighlight(block.text, highlightedExcerpt, highlightedExcerptStart, highlightedExcerptEnd, highlightMarkRef)
                        : block.html
                          ? <span dangerouslySetInnerHTML={{ __html: block.html }} />
                          : block.text}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
});

export default DocumentViewer;